import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

/**
 * Modal for promoters to join an event via its shareable code and create a
 * new guest list underneath it. Two-step flow: lookup code → configure list.
 *
 * Props:
 *   open, onOpenChange       — dialog state
 *   codeInput, setCodeInput  — event code textfield state
 *   codeLookup               — { title, date, venue, my_list_count } or null after lookup succeeds
 *   codeLookupLoading        — spinner while looking up
 *   onLookupCode             — handler that fetches the event by code
 *   onResetLookup            — clears codeLookup so user can try a different code
 *   listForm, setListForm    — form state for the list-creation step
 *   onSubmit                 — final create-list submit handler
 */
export default function CreateListDialog({
  open,
  onOpenChange,
  codeInput,
  setCodeInput,
  codeLookup,
  codeLookupLoading,
  onLookupCode,
  onResetLookup,
  listForm,
  setListForm,
  onSubmit,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-zinc-800 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-['Josefin_Sans'] text-2xl">JOIN EVENT &amp; CREATE LIST</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4 mt-4">
          {!codeLookup ? (
            <>
              <div>
                <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">
                  EVENT CODE
                </label>
                <input
                  type="text"
                  value={codeInput}
                  onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                  placeholder="UN-EVT-2026-XXXX"
                  className="w-full h-12 bg-[#050505] border border-zinc-800 text-white px-4 font-mono focus:border-[#B5FF00] focus:outline-none tracking-wider"
                  data-testid="promoter-event-code-input"
                />
                <p className="text-zinc-500 text-xs mt-2 font-mono">
                  Ask the event organizer for the code shown on their dashboard.
                </p>
              </div>
              <button
                type="button"
                onClick={onLookupCode}
                disabled={codeLookupLoading || !codeInput.trim()}
                className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="promoter-lookup-code-btn"
              >
                {codeLookupLoading ? <Loader2 size={18} className="animate-spin" /> : null}
                JOIN EVENT
              </button>
            </>
          ) : (
            <>
              <div className="border border-[#B5FF00]/30 bg-[#B5FF00]/5 p-3" data-testid="promoter-event-joined">
                <div className="font-mono text-xs text-zinc-400 tracking-widest mb-1">JOINED</div>
                <div className="text-white font-bold">{codeLookup.title}</div>
                <div className="text-zinc-400 text-xs font-mono">
                  {codeLookup.date} · {codeLookup.venue}
                </div>
                {codeLookup.my_list_count > 0 && (
                  <div className="text-amber-400 text-xs font-mono mt-2">
                    You already have {codeLookup.my_list_count} list(s) for this event.
                  </div>
                )}
                <button
                  type="button"
                  onClick={onResetLookup}
                  className="text-zinc-500 hover:text-white font-mono text-xs underline mt-2"
                >
                  USE A DIFFERENT CODE
                </button>
              </div>
              <div>
                <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">LIST NAME</label>
                <input
                  type="text"
                  value={listForm.name}
                  onChange={(e) => setListForm({ ...listForm, name: e.target.value })}
                  placeholder="e.g. VIP, My Crew"
                  className="w-full h-12 bg-[#050505] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none"
                  required
                  data-testid="list-name-input"
                />
              </div>
              <div>
                <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">MAX CAPACITY (optional)</label>
                <input
                  type="number"
                  value={listForm.max_capacity}
                  onChange={(e) => setListForm({ ...listForm, max_capacity: e.target.value })}
                  placeholder="e.g. 50"
                  className="w-full h-12 bg-[#050505] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none"
                  data-testid="list-capacity-input"
                />
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={listForm.wait_list_mode}
                  onChange={(e) => setListForm({ ...listForm, wait_list_mode: e.target.checked })}
                  className="w-5 h-5"
                  data-testid="list-waitlist-input"
                />
                <span className="font-mono text-sm text-zinc-300">
                  Auto-waitlist when full <span className="text-zinc-500">(don&apos;t reject — queue them up)</span>
                </span>
              </label>
              <div className="flex gap-4 pt-4">
                <button type="button" onClick={() => onOpenChange(false)} className="btn-secondary flex-1">CANCEL</button>
                <button type="submit" className="btn-primary flex-1" data-testid="save-list-btn">CREATE LIST</button>
              </div>
            </>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}
